/*###########################################################################
 // Site confirm / prompt dialogs (Promise-based, desk UI)
 //#########################################################################*/

let host = null
let titleEl = null
let messageEl = null
let fieldWrap = null
let inputEl = null
let checklistWrap = null
let checklistEl = null
let confirmBtn = null
let cancelBtn = null
let eyebrowEl = null
let resolveFn = null
/** @type {'confirm' | 'prompt' | 'checklist'} */
let mode = 'confirm'

function ensureHost() {
  if (host && document.body.contains(host)) return

  host = document.createElement('div')
  host.id = 'site-confirm'
  host.className = 'site-confirm'
  host.setAttribute('role', 'dialog')
  host.setAttribute('aria-modal', 'true')
  host.setAttribute('aria-hidden', 'true')
  host.innerHTML = `
    <div class="site-confirm__backdrop" data-confirm-cancel></div>
    <div class="site-confirm__panel" role="document">
      <p class="site-confirm__eyebrow" id="site-confirm-eyebrow">Please confirm</p>
      <h2 class="site-confirm__title" id="site-confirm-title"></h2>
      <p class="site-confirm__message" id="site-confirm-message"></p>
      <div class="site-confirm__field" id="site-confirm-field" hidden>
        <label class="site-confirm__label" for="site-confirm-input" id="site-confirm-label">Name</label>
        <input
          id="site-confirm-input"
          type="text"
          class="field-input site-confirm__input"
          autocomplete="off"
          maxlength="120"
        >
      </div>
      <div class="site-confirm__checklist" id="site-confirm-checklist" hidden>
        <p class="site-confirm__label" id="site-confirm-checklist-label">Options</p>
        <div class="site-confirm__checklist-items" id="site-confirm-checklist-items"></div>
      </div>
      <div class="site-confirm__actions">
        <button type="button" class="btn btn--outline site-confirm__cancel" data-confirm-cancel>Cancel</button>
        <button type="button" class="btn site-confirm__ok" data-confirm-ok>Confirm</button>
      </div>
    </div>
  `

  document.body.appendChild(host)

  titleEl = host.querySelector('#site-confirm-title')
  messageEl = host.querySelector('#site-confirm-message')
  fieldWrap = host.querySelector('#site-confirm-field')
  inputEl = host.querySelector('#site-confirm-input')
  checklistWrap = host.querySelector('#site-confirm-checklist')
  checklistEl = host.querySelector('#site-confirm-checklist-items')
  confirmBtn = host.querySelector('[data-confirm-ok]')
  cancelBtn = host.querySelector('.site-confirm__cancel')
  eyebrowEl = host.querySelector('#site-confirm-eyebrow')

  host.addEventListener('click', (event) => {
    if (event.target.closest('[data-confirm-cancel]')) {
      close(cancelValue())
    }
    if (event.target.closest('[data-confirm-ok]')) {
      submit()
    }
  })

  inputEl.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      submit()
    }
  })

  document.addEventListener('keydown', onKeydown)
}

function cancelValue() {
  if (mode === 'prompt' || mode === 'checklist') return null
  return false
}

function onKeydown(event) {
  if (!host?.classList.contains('is-open')) return
  if (event.key === 'Escape') {
    event.preventDefault()
    close(cancelValue())
  }
}

function submit() {
  if (mode === 'prompt') {
    const value = (inputEl?.value || '').trim()
    if (!value) {
      inputEl?.focus()
      return
    }
    close(value)
    return
  }
  if (mode === 'checklist') {
    const selected = [...checklistEl.querySelectorAll('input[type="checkbox"]:checked')].map(
      (el) => el.value,
    )
    if (selected.length === 0) {
      checklistEl.querySelector('input[type="checkbox"]')?.focus()
      return
    }
    close(selected)
    return
  }
  close(true)
}

function close(result) {
  if (!host) return
  host.classList.remove('is-open')
  host.setAttribute('aria-hidden', 'true')
  window.app?.enableScroll?.()
  const resolve = resolveFn
  resolveFn = null
  if (resolve) resolve(result)
}

function openShell({ eyebrow, title, message, confirmLabel, cancelLabel, danger = false }) {
  if (resolveFn) {
    close(cancelValue())
  }

  eyebrowEl.textContent = eyebrow
  titleEl.textContent = title
  messageEl.textContent = message || ''
  messageEl.hidden = !message
  confirmBtn.textContent = confirmLabel
  cancelBtn.textContent = cancelLabel
  confirmBtn.classList.toggle('site-confirm__ok--danger', danger)
  confirmBtn.classList.toggle('btn--poke', !danger)
  confirmBtn.disabled = false

  host.classList.add('is-open')
  host.setAttribute('aria-hidden', 'false')
  host.setAttribute('aria-labelledby', 'site-confirm-title')
  window.app?.disableScroll?.()
}

/**
 * @param {object} options
 * @param {string} [options.title]
 * @param {string} options.message
 * @param {string} [options.confirmLabel]
 * @param {string} [options.cancelLabel]
 * @param {boolean} [options.danger]
 * @returns {Promise<boolean>}
 */
export function showConfirm({
  title = 'Are you sure?',
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  danger = true,
} = {}) {
  ensureHost()
  mode = 'confirm'
  fieldWrap.hidden = true
  checklistWrap.hidden = true
  inputEl.value = ''
  openShell({
    eyebrow: 'Please confirm',
    title,
    message,
    confirmLabel,
    cancelLabel,
    danger,
  })

  requestAnimationFrame(() => {
    confirmBtn.focus()
  })

  return new Promise((resolve) => {
    resolveFn = resolve
  })
}

/**
 * Styled text prompt — same shell as the confirm dialog.
 *
 * @param {object} options
 * @param {string} [options.title]
 * @param {string} [options.message]
 * @param {string} [options.eyebrow]
 * @param {string} [options.label]
 * @param {string} [options.placeholder]
 * @param {string} [options.initialValue]
 * @param {string} [options.confirmLabel]
 * @param {string} [options.cancelLabel]
 * @param {number} [options.maxLength]
 * @returns {Promise<string|null>} Trimmed value, or null if cancelled
 */
export function showPrompt({
  title = 'Enter a value',
  message = '',
  eyebrow = 'Lists',
  label = 'Name',
  placeholder = '',
  initialValue = '',
  confirmLabel = 'Save',
  cancelLabel = 'Cancel',
  maxLength = 120,
} = {}) {
  ensureHost()
  mode = 'prompt'
  checklistWrap.hidden = true
  fieldWrap.hidden = false
  const labelEl = host.querySelector('#site-confirm-label')
  if (labelEl) labelEl.textContent = label
  inputEl.value = initialValue
  inputEl.placeholder = placeholder
  inputEl.maxLength = maxLength
  openShell({
    eyebrow,
    title,
    message,
    confirmLabel,
    cancelLabel,
    danger: false,
  })

  requestAnimationFrame(() => {
    inputEl.focus()
    inputEl.select()
  })

  return new Promise((resolve) => {
    resolveFn = resolve
  })
}

/**
 * Multi-select checklist dialog — same shell as confirm.
 *
 * @param {object} options
 * @param {string} [options.title]
 * @param {string} [options.message]
 * @param {string} [options.eyebrow]
 * @param {string} [options.label]
 * @param {Array<{value: string, label: string, checked?: boolean}>} options.items
 * @param {string} [options.confirmLabel]
 * @param {string} [options.cancelLabel]
 * @returns {Promise<string[]|null>}
 */
export function showChecklist({
  title = 'Choose options',
  message = '',
  eyebrow = 'Lists',
  label = 'Lists',
  items = [],
  confirmLabel = 'Save',
  cancelLabel = 'Cancel',
} = {}) {
  ensureHost()
  mode = 'checklist'
  fieldWrap.hidden = true
  checklistWrap.hidden = false
  const listLabel = host.querySelector('#site-confirm-checklist-label')
  if (listLabel) listLabel.textContent = label

  checklistEl.innerHTML = ''
  items.forEach((item) => {
    const row = document.createElement('label')
    row.className = 'site-confirm__check'
    const input = document.createElement('input')
    input.type = 'checkbox'
    input.value = String(item.value)
    input.checked = Boolean(item.checked)
    const text = document.createElement('span')
    text.textContent = item.label
    row.append(input, text)
    checklistEl.appendChild(row)
  })

  openShell({
    eyebrow,
    title,
    message,
    confirmLabel,
    cancelLabel,
    danger: false,
  })

  requestAnimationFrame(() => {
    checklistEl.querySelector('input[type="checkbox"]')?.focus() || confirmBtn.focus()
  })

  return new Promise((resolve) => {
    resolveFn = resolve
  })
}

/** Progressive enhancement for forms with data-confirm / data-confirm-title */
export function bindConfirmForms(root = document) {
  root.querySelectorAll('form[data-confirm]').forEach((form) => {
    if (form.dataset.confirmBound) return
    form.dataset.confirmBound = '1'

    form.addEventListener('submit', async (event) => {
      if (form.dataset.confirmAccepted === '1') {
        delete form.dataset.confirmAccepted
        return
      }

      event.preventDefault()
      event.stopImmediatePropagation()

      const ok = await showConfirm({
        title: form.dataset.confirmTitle || 'Are you sure?',
        message: form.dataset.confirm,
        confirmLabel: form.dataset.confirmLabel || 'Delete',
        cancelLabel: form.dataset.cancelLabel || 'Cancel',
        danger: form.dataset.confirmDanger !== '0',
      })

      if (!ok) return

      form.dataset.confirmAccepted = '1'
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit()
      } else {
        form.submit()
      }
    })
  })
}

/** Progressive enhancement for links with data-confirm (e.g. log out) */
export function bindConfirmLinks(root = document) {
  root.querySelectorAll('a[data-confirm]').forEach((link) => {
    if (link.dataset.confirmBound) return
    link.dataset.confirmBound = '1'

    link.addEventListener('click', async (event) => {
      event.preventDefault()
      event.stopImmediatePropagation()

      const ok = await showConfirm({
        title: link.dataset.confirmTitle || 'Are you sure?',
        message: link.dataset.confirm,
        confirmLabel: link.dataset.confirmLabel || 'Confirm',
        cancelLabel: link.dataset.cancelLabel || 'Cancel',
        danger: link.dataset.confirmDanger !== '0',
      })

      if (!ok) return

      window.location.href = link.href
    })
  })
}
